import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Rooms() {
  const [items, setItems] = useState([]);
  const [props, setProps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ property_id: "", room_number: "", capacity: 1, rent_amount: 0, deposit_amount: 0, notes: "" });

  const load = useCallback(async () => {
    const [r1, r2] = await Promise.all([api.get("/rooms"), api.get("/properties")]);
    setItems(r1.data); setProps(r2.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/rooms", {
        ...form,
        capacity: Number(form.capacity),
        rent_amount: Number(form.rent_amount),
        deposit_amount: Number(form.deposit_amount),
      });
      toast.success("Room added");
      setOpen(false);
      setForm({ property_id: "", room_number: "", capacity: 1, rent_amount: 0, deposit_amount: 0, notes: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete room?")) return;
    await api.delete(`/rooms/${id}`); toast.success("Deleted"); load();
  };

  const propName = (id) => props.find((p) => p.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="rooms-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Manage</div>
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter">Rooms & units</h1>
        </div>
        <button onClick={() => setOpen(true)} data-testid="add-room-btn"
                disabled={props.length === 0}
                className="bg-blue-600 text-white px-5 py-3 hover:bg-blue-700 inline-flex items-center gap-2 disabled:opacity-50">
          <Plus className="h-4 w-4" /> Add room
        </button>
      </div>

      {props.length === 0 && <div className="bg-orange-50 border border-orange-200 text-orange-800 p-4">Add a property first.</div>}

      {open && (
        <form onSubmit={submit} className="bg-white border border-slate-200 p-6 grid md:grid-cols-3 gap-4" data-testid="room-form">
          <select required className="border border-slate-300 px-4 py-2.5" value={form.property_id} data-testid="room-property"
                  onChange={(e) => setForm({ ...form, property_id: e.target.value })}>
            <option value="">Select property…</option>
            {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input required placeholder="Room number / name" data-testid="room-number"
                 className="border border-slate-300 px-4 py-2.5"
                 value={form.room_number} onChange={(e) => setForm({ ...form, room_number: e.target.value })} />
          <input type="number" min="1" placeholder="Capacity (beds)" data-testid="room-capacity"
                 className="border border-slate-300 px-4 py-2.5"
                 value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
          <input type="number" min="0" placeholder="Rent ₹" data-testid="room-rent"
                 className="border border-slate-300 px-4 py-2.5"
                 value={form.rent_amount} onChange={(e) => setForm({ ...form, rent_amount: e.target.value })} />
          <input type="number" min="0" placeholder="Deposit ₹" data-testid="room-deposit"
                 className="border border-slate-300 px-4 py-2.5"
                 value={form.deposit_amount} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} />
          <input placeholder="Notes" className="border border-slate-300 px-4 py-2.5"
                 value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <div className="md:col-span-3 flex gap-3">
            <button data-testid="room-save" className="bg-slate-900 text-white px-6 py-2.5">Save</button>
            <button type="button" onClick={() => setOpen(false)} className="bg-slate-100 px-6 py-2.5">Cancel</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200">
        {items.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="rooms-empty">No rooms yet.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-6 py-3">Room</th><th className="px-6 py-3">Property</th>
                <th className="px-6 py-3">Capacity</th><th className="px-6 py-3">Occupied</th>
                <th className="px-6 py-3">Rent</th><th className="px-6 py-3">Deposit</th><th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-6 py-4 font-medium">{r.room_number}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{propName(r.property_id)}</td>
                  <td className="px-6 py-4">{r.capacity}</td>
                  <td className="px-6 py-4">{r.occupied}</td>
                  <td className="px-6 py-4 font-mono">₹{Number(r.rent_amount).toLocaleString("en-IN")}</td>
                  <td className="px-6 py-4 font-mono">₹{Number(r.deposit_amount).toLocaleString("en-IN")}</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => remove(r.id)} className="text-red-600 hover:bg-red-50 p-2"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
